/**
 * Application Configuration Constants
 * 
 * This file contains all the configuration constants used throughout the application.
 * Update these values to change URLs, API endpoints, and other configuration settings.
 */

// Environment-based configuration
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// API Configuration
export const API_CONFIG = {
  // Base API URL - can be overridden by environment variable
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8001',

  // API endpoints
  ENDPOINTS: {
    API_BASE: '/api',
    PROJECTS: '/projects',
    BLOCKS: '/blocks',
    PARCELS: '/parcels',
    MAP_LAYERS: '/map-layers',
    LANDMARKS: '/landmarks',
    STORAGE: '/storage',
  },

  // Full API URL
  get FULL_URL() {
    return `${this.BASE_URL}${this.ENDPOINTS.API_BASE}`;
  },

  // Storage URL for file access
  get STORAGE_URL() {
    return `${this.BASE_URL}${this.ENDPOINTS.STORAGE}`;
  },

  // Map layers file access URL
  get MAP_LAYERS_FILE_URL() {
    return `${this.BASE_URL}${this.ENDPOINTS.API_BASE}/map-layers/file`;
  }
}; 

// DigitalOcean Spaces Configuration
export const SPACES_CONFIG = {
  // DigitalOcean Spaces URLs
  ORIGIN_URL: 'https://s3-cdn-realmap.fra1.digitaloceanspaces.com',
  CDN_URL: 'https://s3-cdn-realmap.fra1.cdn.digitaloceanspaces.com',

  // Environment configuration
  ENVIRONMENT_PREFIX: process.env.REACT_APP_SPACES_ENV_PREFIX || (isDevelopment ? 'staging' : ''),
  // If REACT_APP_SPACES_USE_ENV_PREFIX is explicitly set, use it; otherwise fall back to isDevelopment
  USE_ENV_PREFIX: process.env.REACT_APP_SPACES_USE_ENV_PREFIX !== undefined
    ? process.env.REACT_APP_SPACES_USE_ENV_PREFIX === 'true'
    : isDevelopment,

  // Enable direct access to GeoJSON files from Spaces
  DIRECT_GEOJSON_ACCESS: process.env.REACT_APP_SPACES_DIRECT_ACCESS === 'true',

  // Flag to use API instead of Spaces for residential projects
  USE_API_FOR_RESIDENTIAL: process.env.REACT_APP_USE_API_FOR_RESIDENTIAL === 'true' || true,

  // Get full CDN URL with environment prefix
  get FULL_CDN_URL() {
    const baseUrl = this.CDN_URL;
    const prefix = this.USE_ENV_PREFIX && this.ENVIRONMENT_PREFIX ? `/${this.ENVIRONMENT_PREFIX}` : '';
    return baseUrl + prefix;
  },

  // Get full Origin URL with environment prefix
  get FULL_ORIGIN_URL() {
    const baseUrl = this.ORIGIN_URL;
    const prefix = this.USE_ENV_PREFIX && this.ENVIRONMENT_PREFIX ? `/${this.ENVIRONMENT_PREFIX}` : '';
    return baseUrl + prefix;
  },

  // GeoJSON file patterns (will be prefixed with environment)
  GEOJSON_PATTERNS: {
    COMPRESSED: '{cdn_url}/geojson/parcels/project_{project_id}_compressed.json',
    ULTRA: '{cdn_url}/geojson/parcels/project_{project_id}_ultra.json.gz',
    PRETTY: '{cdn_url}/geojson/parcels/project_{project_id}.json'
  },

  // Generate direct GeoJSON URL with environment prefix using Origin Endpoint
  getGeoJsonUrl: (projectId, type = 'compressed') => {
    const pattern = SPACES_CONFIG.GEOJSON_PATTERNS[type.toUpperCase()] ||
                   SPACES_CONFIG.GEOJSON_PATTERNS.COMPRESSED;

    return pattern
      .replace('{cdn_url}', SPACES_CONFIG.FULL_ORIGIN_URL)
      .replace('{project_id}', projectId);
  },

  // Check if direct access is enabled
  isDirectAccessEnabled: () => {
    return SPACES_CONFIG.DIRECT_GEOJSON_ACCESS &&
           SPACES_CONFIG.ORIGIN_URL &&
           SPACES_CONFIG.ORIGIN_URL !== '';
  },

  // Check if API should be used for residential projects instead of Spaces
  shouldUseApiForResidential: () => {
    return SPACES_CONFIG.USE_API_FOR_RESIDENTIAL;
  }
};

// Mapbox Configuration
export const MAPBOX_CONFIG = {
  // Mapbox access token
  ACCESS_TOKEN: process.env.REACT_APP_MAPBOX_TOKEN ||
    "pk.eyJ1IjoiaWRlemluZWxhYiIsImEiOiJjbWFqdHVyY28wazJoMnBzaXlxaG93bnZpIn0.DNiC2WOG_d5e_xCCtJMlkA",

  // Default map settings (fallback values - will be overridden by dynamic settings)
  DEFAULT_CENTER: [2.4089386,69.2682553], // Saudi Arabia center
  DEFAULT_ZOOM: 5,
  
  // Map styles
  STYLES: {
    SATELLITE: 'satellite-v9', // Custom satellite style (uses local JSON)
    SATELLITE_CUSTOM: 'satellite-custom', // Custom satellite style
    SATELLITE_STREETS: 'satellite-streets-v12', // Satellite with street details
    STREETS: 'streets-v11',
    OUTDOORS: 'outdoors-v11'
  },

  // Map style URLs
  get STYLE_URLS() {
    return {
      SATELLITE: `mapbox://styles/mapbox/${this.STYLES.SATELLITE}`,
      SATELLITE_CUSTOM: `${window.location.origin}/styles/satellite-custom.json`,
      SATELLITE_STREETS: `mapbox://styles/mapbox/${this.STYLES.SATELLITE_STREETS}`,
      STREETS: `mapbox://styles/mapbox/${this.STYLES.STREETS}`,
      OUTDOORS: `mapbox://styles/mapbox/${this.STYLES.OUTDOORS}`
    };
  }
};

// Application URLs and Paths
export const APP_PATHS = {
  // Public assets
  PUBLIC_ASSETS: {
    LOGO: '/logo v2.png',
    LOGO_ONLY: '/logo_only v2.png',
    MAP_ICONS: '/map_icons',
  },
  
  // Map icon paths - Updated to use animated GIF icons
  MAP_ICONS: {
    MOSQUE: '/map_icons/animated/Mosque/dome.gif',
    SCHOOL: '/map_icons/animated/School/school.gif',
    HOSPITAL: '/map_icons/animated/Hospital/hospital.gif',
    SHOPPING_CENTER: '/map_icons/animated/Mall/mall.gif',
    PARK: '/map_icons/animated/Green Area/green-planet.gif',
    GAS_STATION: '/map_icons/animated/Gaz station Fuel/fuel-station.gif',
    RESTAURANT: '/map_icons/animated/Resturant/restaurant.gif',
    BANK: '/map_icons/animated/Mall/mall.gif', // Using mall icon for bank
    PHARMACY: '/map_icons/animated/Pharmacy/medicine.gif',
    CLINIC: '/map_icons/animated/Clinic/clinic.gif',
    POLICE: '/map_icons/animated/Police/police.gif',
    FIRE_STATION: '/map_icons/animated/Fire Station/fire-station.gif',
    HOTEL: '/map_icons/animated/Hotel/hotel-sign.gif',
    GYM: '/map_icons/animated/Gym/gym.gif',
    SWIMMING_POOL: '/map_icons/animated/Swimming Pool/swimming-pool.gif',
    WALKING_PATH: '/map_icons/animated/Walking Path/walk.gif',
    WATER: '/map_icons/animated/Water/splash.gif',
    BEACH: '/map_icons/animated/Beach/beach.gif',
    ENTRANCE: '/map_icons/animated/Entrance/graffiti.gif',
    CULTURE_CENTER: '/map_icons/animated/Culture center or Cummunity center/corporate-culture.gif',
    PARKING: '/map_icons/animated/Parking/parking.gif',
    OTHER: '/map_icons/animated/Mall/mall.gif',

    // Default fallback icon
    get DEFAULT() {
      return this.OTHER;
    }
  }
};

// Stripe Configuration
export const STRIPE_CONFIG = {
  // Stripe public key - can be overridden by environment variable
  PUBLIC_KEY: process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_51QQCMUE4kNi4RbPfoOnwfJtvlk9yDKPmB4ZnJOkFn8yt2c2lJhSXLjc8EKYBPEimYqGVeMQHjupX5xOzARJ0fZei00nVDpuRSe',
  
  // Stripe settings
  CURRENCY: 'sar', // Saudi Riyal
  COUNTRY: 'SA',
  
  // Payment options
  PAYMENT_METHODS: ['card'],
  LOCALE: 'ar' // Default locale
};

// Application Settings
export const APP_SETTINGS = {
  // Default language
  DEFAULT_LANGUAGE: 'ar',
  
  // Supported languages
  SUPPORTED_LANGUAGES: ['ar', 'en'],
  
  // Default map style
  DEFAULT_MAP_STYLE: MAPBOX_CONFIG.STYLES.SATELLITE,
  
  // Loading and animation settings
  LOADING_TIMEOUT: 2000,
  ANIMATION_DURATION: 400,
  
  // Map settings
  MAP: {
    MIN_ZOOM: 1,
    MAX_ZOOM: 22,
    LANDMARK_MIN_ZOOM: 14,
    PROJECT_MARKER_HIDE_ZOOM: 12,
    STREET_DETAILS_ZOOM: 14, // Zoom level to switch to detailed satellite view
  }
};

// Development/Production specific settings
export const ENV_CONFIG = {
  IS_DEVELOPMENT: isDevelopment,
  IS_PRODUCTION: isProduction,
  
  // Enable/disable features based on environment
  FEATURES: {
    DEBUG_MODE: isDevelopment,
    CONSOLE_LOGS: isDevelopment,
    ERROR_REPORTING: isProduction,
  }
};

// Helper functions
export const getApiUrl = (endpoint = '') => {
  return `${API_CONFIG.FULL_URL}${endpoint}`;
};

export const getStorageUrl = (path = '') => {
  return `${API_CONFIG.STORAGE_URL}/${path}`;
};

export const getMapLayerFileUrl = (filename) => {
  // Check if direct Spaces access is enabled
  if (SPACES_CONFIG.isDirectAccessEnabled()) {
    // Try to construct Spaces URL for map layers with environment prefix using Origin Endpoint
    const spacesPath = `${SPACES_CONFIG.FULL_ORIGIN_URL}/map-layers/${filename}`;
    return spacesPath;
  }

  // Fallback to API
  return `${API_CONFIG.MAP_LAYERS_FILE_URL}/${filename}`;
};

export const getMapIconPath = (iconType) => {
  return APP_PATHS.MAP_ICONS[iconType.toUpperCase()] || APP_PATHS.MAP_ICONS.DEFAULT;
};

// Export default configuration object
export default {
  API_CONFIG,
  MAPBOX_CONFIG,
  STRIPE_CONFIG,
  APP_PATHS,
  APP_SETTINGS,
  ENV_CONFIG,
  // Helper functions
  getApiUrl,
  getStorageUrl,
  getMapLayerFileUrl,
  getMapIconPath
};
